/**
 * Warm the chunks behind Platform Settings and Company Settings before they
 * are asked for.
 *
 * Opening either settings layer used to be a waterfall of round trips, each
 * one a visible step in the panel: the modal's own chunk (nothing on screen
 * for ~400ms after the tap), then the section's page chunk (the panel's
 * "Loading…" fallback), and switching sections in the rail paid the page
 * chunk again every time. On an iPad over the network each step is longer
 * than in the harness, where it was measured.
 *
 * The components below ARE the routes' components (App.tsx renders them), so
 * a warm-up here means the route renders on its first commit with no
 * fallback at all. Failures are swallowed: this is a head start, and the
 * route's own render surfaces any error through its ChunkErrorBoundary.
 *
 * None of this is on the first-load path: a dynamic import is never a
 * modulepreload, so `npm run bundle-budget` is unaffected.
 */

import { lazyPreload, type Preloadable } from "@/lib/lazyPreload";
export { whenIdle } from "@/lib/lazyPreload";

const quiet = (p: Preloadable) => { void p.preload().catch(() => {}); };

/* The two layers and every section page, as App.tsx renders them. They live
   here, not in App.tsx, so the sidebar and the rail can warm them without
   importing the router — and warming goes through the SAME lazy each route
   renders, which is the only way a warm chunk renders without suspending
   (see lib/lazyPreload.ts). */
export const PlatformModalPage = lazyPreload(() => import("@/components/PlatformModal").then((m) => ({ default: m.PlatformModal })));
export const CompanyModalPage = lazyPreload(() => import("@/components/CompanyModal").then((m) => ({ default: m.CompanyModal })));

export const UsagePagePage = lazyPreload(() => import("@/pages/UsagePage"));
export const CompaniesPage = lazyPreload(() => import("@/pages/Companies"));
export const ProjectListPage = lazyPreload(() => import("@/pages/ProjectList"));
export const UsersPage = lazyPreload(() => import("@/pages/Users"));
export const RolesPage = lazyPreload(() => import("@/pages/Roles"));
export const UserLogPagePage = lazyPreload(() => import("@/pages/UserLogPage"));
export const QuotesPage = lazyPreload(() => import("@/pages/Quotes"));
export const PlatformReportsPage = lazyPreload(() => import("@/pages/PlatformReports"));
export const CompetitorAnalysisPage = lazyPreload(() => import("@/pages/CompetitorAnalysis"));
export const AdminRoadmapPage = lazyPreload(() => import("@/pages/Roadmap"));
export const IntegrationsPage = lazyPreload(() => import("@/pages/Integrations"));
export const DocumentLibraryPage = lazyPreload(() => import("@/pages/DocumentLibrary"));
export const AppSettingsPage = lazyPreload(() => import("@/pages/AppSettings"));
export const CompanyAdminPage = lazyPreload(() => import("@/pages/CompanyAdmin"));

/** Section href prefix → the page it renders. Longest prefix wins. */
const SECTION_PAGES: Array<[string, Preloadable]> = ([
  ["/admin/usage", UsagePagePage],
  ["/admin/companies", CompaniesPage],
  ["/admin/projects", ProjectListPage],
  ["/admin/users", UsersPage],
  ["/admin/roles", RolesPage],
  ["/admin/user-log", UserLogPagePage],
  ["/admin/billing", QuotesPage],
  ["/admin/reports", PlatformReportsPage],
  ["/admin/competitor-analysis", CompetitorAnalysisPage],
  ["/admin/roadmap", AdminRoadmapPage],
  ["/admin/integrations", IntegrationsPage],
  ["/admin/documents", DocumentLibraryPage],
  ["/admin/settings", AppSettingsPage],
  ["/company-admin", CompanyAdminPage],
] as Array<[string, Preloadable]>).sort((a, b) => b[0].length - a[0].length);

/** Fetch the page a settings section renders, if it is one we know. */
export function preloadSettingsSection(href: string): void {
  const path = href.split("?")[0];
  const hit = SECTION_PAGES.find(([prefix]) => path === prefix || path.startsWith(prefix + "/"));
  if (hit) quiet(hit[1]);
}

/** The Platform Settings modal and its opening section (the rail's first, Usage). */
export function preloadPlatformSettings(): void {
  quiet(PlatformModalPage);
  preloadSettingsSection("/admin/usage");
}

/** The Company Settings modal and its opening section (Company Details). */
export function preloadCompanySettings(): void {
  quiet(CompanyModalPage);
  preloadSettingsSection("/company-admin");
}
